/**
 * ShiftLadder: the 16-LED shift strip as a state machine that the GEARBOX obeys.
 *
 * Marc, from the driver's seat, four times over: the lights skip rungs, the blue arrives before the strip
 * is complete, a change lands while the strip is half lit ("como una escopeta de feria"). The dash had
 * been REACTING to the revs, and no reaction can be both complete and on time in a gear that climbs three
 * times faster than the next one. So the strip leads now: it walks one LED at a time, the full strip is
 * SEEN, the blue blinks, and only then does the automatic box change up. The change is nailed to the
 * lights by construction, in every gear and with every gear ratio, because the box waits for the lights
 * instead of the lights chasing the box.
 *
 * Per gear the strip spans [where an upshift lands this gear, the rpm the box will change at], so it
 * fills exactly once per gear whatever the ratios (Marc: "me da igual qué gear ratio haya"). Under a
 * steady climb the full point is brought forward by the time the hold and the blue need, so the change
 * still comes at the approved rpm; when the climb is not steady the box simply waits the remainder.
 *
 * The order, religiously: dark, 1 green a side, 2, 3, +1 yellow, +2, +3, +1 red, +2 red (full), blue
 * blinking, the change, dark, again. An upshift goes dark at once. A downshift shows where the revs land
 * and then follows them down one LED at a time, so engine braking lights whatever the revs say.
 *
 * Guarded by shiftLadder.test.ts, which drives the real drivetrain through both boxes and an ordinary
 * car's ratios and asserts every one of these sentences.
 */
export interface LadderInput {
  /** seconds since the last update */
  dt: number;
  engineOn: boolean;
  rpm: number;
  idleRpm: number;
  /** the rpm the box will change up at right now (the limiter in RACE, well under it in DRIVE) */
  refRpm: number;
  gear: number;
  gearTops: readonly number[];
  shifting: boolean;
  shiftDir: -1 | 0 | 1;
  /** sitting on the rev limiter (not the pit limiter): the top rung is held whatever the chop does to the revs */
  atCut: boolean;
  launch: boolean;
  /** the box has decided to change up and is waiting for the strip to finish its show */
  wantUp: boolean;
  /** RACE: the change comes at the blue, so the strip runs ahead to finish its show there. Off (DRIVE,
   *  manual) it is a plain rev strip: the rungs sit where they sit and the blue means the limit itself */
  raceShow: boolean;
  /** the box is deliberately holding its gear (launch, getaway): the strip follows the revs but never
   *  goes blue, because the blue is a promise that the change is coming */
  holdGear?: boolean;
}

export interface LadderState {
  /** LEDs lit per side, 0..8 (3 green, 3 yellow, 2 red from the outside in) */
  stage: number;
  /** blue phase: the whole strip strobes blue */
  blue: boolean;
  /** blacked out through a gearchange */
  dark: boolean;
}

export const LADDER_STEPS = 8;
const STEP_UP_S = 0.085;     // one LED at a time on the way up: quick enough for a short gear, slow enough to be seen
const STEP_DOWN_S = 0.11;    // and on the way down, following the revs
const FULL_HOLD_S = 0.22;    // the complete strip (centre reds in) is SEEN before the blue takes over
const BLUE_MIN_S = 0.33;     // two blinks at 6.5 Hz; the change waits for them
const DARK_S = 0.32;         // blue to black straight through the change and into the new gear
// THE BOX IS ASKING: the driver floored it late, or the estimate was low, and the revs are at the change
// point with the show unfinished. The order is kept but the show compresses: a quick walk, a short hold,
// one blink. Marc: 'NUNCA debería en AUTO cambiar llegando al limitador'. The box has its own hard stop
// just under the cut, so this is what keeps the lights AND the change honest at the same time.
const STEP_UP_FAST_S = 0.045;
const FULL_HOLD_FAST_S = 0.10;
const BLUE_MIN_FAST_S = 0.16;
// under a steady climb the strip completes this long before the change: the hold, the blue, and a wide
// margin so a rate read low (GPS-fed revs are never a straight line) still leaves the blue done well
// before the change point. The lights must be early rather than late, never the other way round.
const LEAD_S = FULL_HOLD_S + BLUE_MIN_S + 0.45;
const LEAD_MAX_FRAC = 0.2;   // of the change point: a wild rate estimate cannot drag the full point down the range
// the strip needs TIME to be walked (8 rungs) and the natural band of a tall or compressed gear can be
// too short for it at speed: the band then reaches further down the range so the whole show still fits
const SPAN_S = LADDER_STEPS * STEP_UP_S + 0.25;
const CLIMB_RPM_S = 150;     // revs rising faster than this count as a climb (the blue needs one, or the limiter)
// an LED goes off well below where it came on. It was 2 % of the band, which in a tall gear is 15 rpm:
// the jitter of GPS-fed revs unlit the top rung, reset the hold, and the blue came late (Marc, in the car)
const HYS_BAND = 0.02;
const HYS_REF = 0.03;

export class ShiftLadder {
  stage = 0;
  blue = false;
  dark = false;
  private sinceStep = 0;
  private fullFor = 0;
  private blueFor = 0;
  private darkLeft = 0;
  private refS = 0;
  private topS = 0;
  private rate = 0;
  private prevRpm = -1;
  private wasUp = false;
  private sinceShift = 10;
  private snapLeft = 0;
  private blueMin = BLUE_MIN_S;
  private leadHeld = 0;    // the lead only GROWS within a gear: a rate estimate that wobbles must not move the rungs back up
  private spanHeld = 0;    // same for the reach of the band

  /** the show is complete: the strip was full, the blue has blinked. The box may change now. */
  get ready(): boolean {
    return this.blue && this.blueFor >= this.blueMin;
  }

  update(i: LadderInput): LadderState {
    const dt = i.dt;
    if (!i.engineOn) {
      this.stage = 0; this.blue = false; this.dark = false;
      this.fullFor = 0; this.blueFor = 0; this.darkLeft = 0; this.snapLeft = 0; this.rate = 0; this.prevRpm = -1; this.leadHeld = 0; this.spanHeld = 0; this.topS = 0;
      return this.out();
    }
    // the reference is eased: in DRIVE it moves with the throttle estimate every tick
    this.refS = this.refS > 0 ? this.refS + (i.refRpm - this.refS) * Math.min(1, dt / 0.2) : i.refRpm;
    // rate of climb, smoothed; not measured through a change (the drop through a shift is not a climb)
    this.sinceShift = i.shifting ? 0 : this.sinceShift + dt;
    if (this.prevRpm >= 0 && !i.shifting && this.sinceShift > 0.15) {
      const dr = (i.rpm - this.prevRpm) / Math.max(0.001, dt);
      this.rate += (dr - this.rate) * Math.min(1, dt / 0.2);
    } else {
      this.rate = 0;
    }
    this.prevRpm = i.rpm;
    // the band: from where an upshift lands this gear to the change point. 1st starts at idle.
    const tops = i.gearTops;
    const g = Math.max(1, Math.min(i.gear, tops.length));
    // 1st has no gear below it: it gets the 1st-to-2nd step as its band, so it reads like every other gear
    // (from idle it spanned the whole range and DRIVE's calm change point sat in the reds)
    const ratio = g > 1 ? tops[g - 2] / tops[g - 1] : tops.length > 1 ? tops[0] / tops[1] : 0.75;
    // the plain rev strip (DRIVE, manual) uses FIXED thresholds like a real dash, from 70 % of the blue
    // point: DRIVE flat out then changes on three greens and a yellow a side (Marc's example).
    // LAUNCH is its own show and the strip belongs to it: the band opens at idle so the whole sequence is
    // readable — the revs climb, dip and climb again, and the LEDs do exactly that with them (Marc: 'esa
    // subida de rpm no está bien coordinada con las luces'). Without this the sweep lit nothing until
    // 5.7k, then jumped, and the dip fell off the bottom of the strip.
    let entry = i.launch ? i.idleRpm : this.refS * (i.raceShow ? ratio : 0.70);
    entry = Math.max(i.idleRpm, Math.min(entry, this.refS * 0.9));   // a close pair of ratios still leaves a strip to climb
    // within a gear the rungs only ever move DOWN the range (a growing lead, a longer reach); the wobble
    // of a GPS-fed rate estimate used to move them back up and the strip stepped backwards mid-pull
    if (i.raceShow && !i.launch) {
      this.leadHeld = Math.max(this.leadHeld, Math.min(this.refS * LEAD_MAX_FRAC, this.rate * LEAD_S));
      this.spanHeld = Math.max(this.spanHeld, Math.max(0, this.rate) * SPAN_S);
    } else {
      // no prediction inside a launch: the strip is a rev counter there, nothing is coming
      this.leadHeld = 0; this.spanHeld = 0;
    }
    const top = this.refS - this.leadHeld;
    this.topS = this.topS > 0 ? this.topS + (top - this.topS) * Math.min(1, dt / 0.15) : top;
    // the band reaches at least as far down as the walk needs at this rate of climb
    entry = Math.max(i.idleRpm, Math.min(entry, this.topS - this.spanHeld));
    // the launch sweep moves faster than a walk: its LEDs follow the revs rather than a step clock
    const launchStep = i.launch;
    const span = Math.max(1, this.topS - entry);
    const hys = Math.max(HYS_BAND * span, HYS_REF * this.refS);
    let up = 0, down = 0;
    for (let k = 1; k <= LADDER_STEPS; k++) {
      const thr = entry + (k / LADDER_STEPS) * span;
      if (i.rpm >= thr) up = k;              // rungs the revs have reached
      if (i.rpm >= thr - hys) down = k;      // rungs the revs still hold
    }
    if (i.atCut) { up = LADDER_STEPS; down = LADDER_STEPS; }   // the chop bounces the revs; the top rung stays lit

    // ---- the sequence ----
    this.sinceStep += dt;
    const upshiftNow = i.shiftDir > 0;
    if (upshiftNow && !this.wasUp) {
      // AN UPSHIFT GOES DARK AT ONCE (Marc): blue, black, and the new gear starts from nothing
      this.darkLeft = DARK_S; this.stage = 0; this.blue = false; this.blueFor = 0; this.fullFor = 0;
      this.leadHeld = 0; this.spanHeld = 0; this.topS = 0;   // a new gear, a new band
    }
    this.wasUp = upshiftNow;
    if (this.darkLeft > 0) {
      this.darkLeft -= dt;
      this.stage = 0; this.blue = false; this.dark = true;
      return this.out();
    }
    this.dark = false;
    // a downshift is a step, not a sweep: the strip shows where the revs land, and keeps showing it while
    // the engine catches up to the new gear (a few tenths), before it starts following them down
    if (i.shiftDir < 0) { this.snapLeft = 0.3; this.leadHeld = 0; this.spanHeld = 0; this.topS = 0; }
    if (this.snapLeft > 0) {
      if (i.shiftDir >= 0) this.snapLeft -= dt;
      this.stage = Math.min(up, LADDER_STEPS); this.blue = false; this.blueFor = 0; this.fullFor = 0; this.sinceStep = 0;
      return this.out();
    }
    // launch control holds the revs just under the top: the centre reds stay for the flicker, never blue
    const cap = i.launch ? LADDER_STEPS - 1 : LADDER_STEPS;
    const stepUp = launchStep ? 0.05 : i.wantUp ? STEP_UP_FAST_S : STEP_UP_S;
    const hold = i.wantUp ? FULL_HOLD_FAST_S : FULL_HOLD_S;
    this.blueMin = i.wantUp ? BLUE_MIN_FAST_S : BLUE_MIN_S;
    if (up > this.stage && this.stage < cap && this.sinceStep >= stepUp) {
      this.stage++; this.sinceStep = 0;
    } else if (down < this.stage && (this.rate <= 0 || launchStep) && this.sinceStep >= (launchStep ? 0.06 : STEP_DOWN_S) && !(this.blue && this.blueFor < this.blueMin)) {
      // …and only while the revs are actually falling: a strip never steps back mid-pull (Marc)
      this.stage--; this.sinceStep = 0;
    }
    if (this.stage > cap) this.stage = cap;
    this.fullFor = this.stage >= LADDER_STEPS ? this.fullFor + dt : 0;
    if (i.launch || i.holdGear) { this.blue = false; this.blueFor = 0; return this.out(); }
    if (!this.blue) {
      // the blue needs the full strip SEEN, and a reason: the box asking, the limiter, the revs at the
      // point, or a climb that will reach it within a beat. The strip runs early on purpose, so without
      // the last condition the blue would open with the full strip and sit there for seconds in a tall gear
      const ttl = i.raceShow && this.rate > CLIMB_RPM_S ? (this.refS - i.rpm) / this.rate : Infinity;
      const reason = !i.holdGear && (i.wantUp || i.atCut || i.rpm >= this.refS * 0.985 || ttl <= BLUE_MIN_S + 0.35);
      if (this.stage >= LADDER_STEPS && this.fullFor >= hold && reason) { this.blue = true; this.blueFor = 0; }
    } else {
      this.blueFor += dt;
      // the latched blinks are never cut short; after them the phase ends only once the revs fall off the top rung
      if (this.blueFor >= this.blueMin && !i.wantUp && this.stage < LADDER_STEPS) this.blue = false;
    }
    return this.out();
  }

  private out(): LadderState {
    return { stage: this.stage, blue: this.blue, dark: this.dark };
  }
}
